import React, { useMemo, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { SectionCard, SectionHeader, SectionLoading, LegendItem } from './ui/Section';
import { useChartStyles, ChartTooltipCard, ChartLoadingOverlay } from './ui/chartStyles';
import { BrandLoveData } from '../hooks/useBrandLoveData';
import { fetchBrandColors, getBrandColors } from '../utils/brandColors';
import { useAxiosClient } from '../utils/useAxiosClient';
import dayjs from 'dayjs';
import { useFilterContext } from '../context/FilterContext';

const BRAND_LOVE_INFO =
  'Brand Love applies a 30-day rolling calculation to various engagement metrics for each brand. These Brand Love scores are standardized to allow effortless brand comparison. Post volume does not intrinsically affect the Brand Love score.';

interface BrandLoveCardProps {
  data: BrandLoveData | null;
  selectedBrands: string[];
  startDate: string | null;
  endDate: string | null;
  loading?: boolean;
}

// Helper function to determine the best date format based on the date range
const getDateFormat = (dates: string[]): string => {
  if (!dates.length) return 'MMM YYYY'; // Default format
  
  // Parse the dates
  const parsedDates = dates.map(d => dayjs(d));
  
  // Check if all dates are in the same month and year
  const firstDate = parsedDates[0];
  const allSameMonth = parsedDates.every(date => 
    date.month() === firstDate.month() && 
    date.year() === firstDate.year()
  );
  
  // Check if all dates are in the same year
  const allSameYear = parsedDates.every(date => date.year() === firstDate.year());
  
  if (allSameMonth) {
    // If all dates are in the same month, show the day
    return 'MMM D';
  } else if (allSameYear) {
    // If all dates are in the same year, show month and day
    return 'MMM D';
  } else {
    // Default format for dates spanning multiple years
    return 'MMM YYYY';
  }
};

// Helper to calculate optimal tick interval based on data size
const calculateTickInterval = (dataLength: number): number | "preserveStartEnd" => {
  if (dataLength <= 7) return 0; // Show all ticks for a week or less
  if (dataLength <= 14) return 1; // Every other day for two weeks
  if (dataLength <= 31) return 2; // Every third day for a month
  if (dataLength <= 60) return 4; // Every fifth day for two months
  return "preserveStartEnd"; // For longer periods, preserve start and end
};

const BrandLoveCard: React.FC<BrandLoveCardProps> = ({ 
  data,
  selectedBrands = [],
  startDate,
  endDate,
  loading = false
}) => {
  const { allBrands } = useFilterContext();
  const axiosClient = useAxiosClient();
  // Brand colors state
  const [brandColors, setBrandColors] = useState<{ [key: string]: string }>({});
  const [colorsLoading, setColorsLoading] = useState(true);
  const [colorRefreshKey, setColorRefreshKey] = useState(0);

  // Load brand colors
  useEffect(() => {
    const loadBrandColors = async () => {
      if (!data) return;
      
      try {
        setColorsLoading(true);
        const brandColorConfig = await fetchBrandColors(axiosClient);
        const colors = await getBrandColors(axiosClient, data.blove_chart.brands, allBrands, brandColorConfig);
        setBrandColors(colors as { [key: string]: string });
      } catch (error) {
        console.error('Error loading brand colors:', error);
        // Fallback to original color scheme
        const fallbackColors: { [key: string]: string } = {};
        if (data?.blove_chart?.brands) {
          data.blove_chart.brands.forEach((brand, index) => {
            fallbackColors[brand] = `hsl(${index * 45}, 70%, 50%)`;
          });
        }
        setBrandColors(fallbackColors);
      } finally {
        setColorsLoading(false);
      }
    };

    loadBrandColors();
  }, [data, allBrands, axiosClient, colorRefreshKey]);

  // Listen for brand color updates via custom event
  useEffect(() => {
    const handleBrandColorsUpdated = () => {
      // Only increment refresh key if we actually have data to refresh
      if (data?.blove_chart?.brands) {
        setColorRefreshKey(prev => prev + 1);
      }
    };

    window.addEventListener('brandColorsUpdated', handleBrandColorsUpdated);
    return () => {
      window.removeEventListener('brandColorsUpdated', handleBrandColorsUpdated);
    };
  }, [data]);

  // Memoize the filtered brands to prevent unnecessary recalculations
  const filteredBrands = useMemo(() => {
    if (!data) return [];
    return selectedBrands.length > 0 
      ? data.blove_chart.brands.filter(brand => selectedBrands.includes(brand))
      : data.blove_chart.brands;
  }, [data, selectedBrands]);

  // Get filtered date range indexes
  const dateRangeIndexes = useMemo(() => {
    if (!data) return { startIndex: 0, endIndex: 0 };
    
    const { dates } = data.blove_chart;
    let startIndex = 0;
    let endIndex = dates.length - 1;
    
    // Filter by start date if provided
    if (startDate) {
      const start = dayjs(startDate);
      startIndex = dates.findIndex(date => dayjs(date).isAfter(start) || dayjs(date).isSame(start));
      if (startIndex === -1) startIndex = 0;
    }
    
    // Filter by end date if provided
    if (endDate) {
      const end = dayjs(endDate);
      // Find the last date that's before or equal to the end date
      for (let i = dates.length - 1; i >= 0; i--) {
        if (dayjs(dates[i]).isBefore(end) || dayjs(dates[i]).isSame(end)) {
          endIndex = i;
          break;
        }
      }
    }
    
    return { startIndex, endIndex };
  }, [data, startDate, endDate]);

  // Memoize the chart data to prevent unnecessary recalculations
  const chartData = useMemo(() => {
    if (!data) return [];
    const { blove_chart } = data;
    
    // Create a map of brand indices for O(1) lookup
    const brandIndices = new Map(
      blove_chart.brands.map((brand, index) => [brand, index])
    );

    // Only use dates within the selected range
    const { startIndex, endIndex } = dateRangeIndexes;
    const filteredDates = blove_chart.dates.slice(startIndex, endIndex + 1);
    
    // Generate chart data points
    const chartPoints = filteredDates.map((date, dateIndex) => {
      const adjustedDateIndex = dateIndex + startIndex; // Adjust to account for the slice
      const dataPoint: { [key: string]: any } = { 
        // Store raw date value for internal usage
        fullDate: date,
        // Use formatted date as the display key
        date: dayjs(date).format('MMM D YYYY'),
        // Add index for X-axis positioning
        index: dateIndex
      };
      
      filteredBrands.forEach(brand => {
        const brandIndex = brandIndices.get(brand);
        if (brandIndex !== undefined) {
          dataPoint[brand] = blove_chart.values[brandIndex][adjustedDateIndex];
        }
      });
      return dataPoint;
    });
    
    return chartPoints;
  }, [data, filteredBrands, dateRangeIndexes]);

  // Generate one tick label per month
  const monthTicks = useMemo(() => {
    if (!chartData.length) return [];
    
    const months: { value: number; label: string }[] = [];
    let currentMonthYear = '';
    
    // Find the positions for each month
    chartData.forEach((point, index) => {
      const monthYear = dayjs(point.fullDate).format('MMM YYYY');
      if (monthYear !== currentMonthYear) {
        currentMonthYear = monthYear;
        months.push({ 
          value: index,
          label: monthYear
        });
      }
    });
    
    return months;
  }, [chartData]);

  // Prepare X-axis labels
  const xAxisLabels = useMemo(() => {
    if (chartData.length === 0) return [];
    
    // Create an array of empty strings with the same length as chartData
    // We'll only fill in the positions that should have labels
    const labels = Array(chartData.length).fill('');
    
    // Set the labels at the positions defined in monthTicks
    monthTicks.forEach(tick => {
      labels[tick.value] = tick.label;
    });
    
    return labels;
  }, [chartData, monthTicks]);

  const cs = useChartStyles();

  const header = (
    <SectionHeader title="Brand Love" info={BRAND_LOVE_INFO} />
  );

  // Show loading skeleton when no data but loading
  if (!data && loading) {
    return (
      <SectionCard>
        {header}
        <SectionLoading label="Loading Brand Love data..." height={380} />
      </SectionCard>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <SectionCard>
      {header}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1, mb: 2.5 }}>
        {filteredBrands.map((brand) => (
          <LegendItem key={brand} color={brandColors[brand] || '#999'} label={brand} />
        ))}
      </Box>
      <Box sx={{ width: '100%', height: 380, position: 'relative' }}>
        {loading && <ChartLoadingOverlay />}
        <ResponsiveContainer>
          <LineChart
            data={chartData}
            margin={{
              top: 8,
              right: 16,
              left: 8,
              bottom: 8,
            }}
          >
            <CartesianGrid {...cs.grid} />
            <XAxis
              {...cs.xAxis}
              dataKey="index"
              ticks={monthTicks.map(m => m.value)}
              tickFormatter={(value) => {
                const matchingTick = monthTicks.find(t => t.value === value);
                return matchingTick ? matchingTick.label : '';
              }}
            />
            <YAxis
              {...cs.yAxis}
              width={56}
              label={cs.axisLabel('Brand Love score')}
            />
            <RechartsTooltip
              cursor={cs.lineCursor}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  // Sort payload by value in descending order
                  const sortedPayload = [...payload].sort((a, b) => {
                    const aValue = (a as any).value || 0;
                    const bValue = (b as any).value || 0;
                    return bValue - aValue;
                  });

                  // Get the fullDate from the payload data
                  const dataPoint = payload[0]?.payload;
                  const dateLabel = dataPoint?.fullDate
                    ? dayjs(dataPoint.fullDate).format('MMM D, YYYY')
                    : label;

                  return (
                    <ChartTooltipCard
                      title={dateLabel}
                      rows={sortedPayload.map((entry: any) => ({
                        key: entry.name,
                        color: entry.color,
                        label: entry.name,
                        value: entry.value.toFixed(2),
                      }))}
                    />
                  );
                }
                return null;
              }}
            />
            {filteredBrands.map((brand) => {
              // Get the brand's original index for consistent color assignment
              const originalIndex = data.blove_chart.brands.indexOf(brand);
              return (
                <Line
                  key={brand}
                  type="monotone"
                  dataKey={brand}
                  stroke={brandColors[brand] || `hsl(${originalIndex * 45}, 70%, 50%)`}
                  dot={false}
                  activeDot={{ r: 3.5, strokeWidth: 0 }}
                  isAnimationActive={false}
                  strokeWidth={2}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </SectionCard>
  );
};

export default BrandLoveCard; 
